import { useState } from 'react'
import { productTotal, type Product } from '@/services/shopService'

interface ProductCardProps {
  product: Product
  onAdd: (product: Product, size: string) => void
  inCart: boolean
  isPurchased: boolean
  userCoins: number
  isLoading?: boolean
}

export default function ProductCard({
  product,
  onAdd,
  inCart,
  isPurchased,
  userCoins,
  isLoading,
}: ProductCardProps) {
  const [failedImage, setFailedImage] = useState<string | null>(null)
  const [size, setSize] = useState(product.sizes.length === 1 ? product.sizes[0] : '')
  const total = productTotal(product)
  const missing = Math.max(0, total - userCoins)

  return (
    <article className="ac-product" aria-labelledby={`product-${product.id}`}>
      <div className="ac-product-image">
        {product.imageUrl && failedImage !== product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            loading="lazy"
            onError={() => setFailedImage(product.imageUrl)}
          />
        ) : (
          <span>Fotografie zatím není k dispozici.</span>
        )}
      </div>
      <div className="ac-product-body">
        <div className="ac-product-kind">
          <span>{product.requiresDelivery ? 'S doručením' : 'Bez doručení'}</span>
          {isPurchased && <strong>Zakoupeno</strong>}
        </div>
        <h3 id={`product-${product.id}`}>{product.name}</h3>
        <p className="ac-product-description">{product.description}</p>
        <div className="ac-product-cost">
          <p>
            <strong>{total.toLocaleString('cs-CZ')}</strong> mincí celkem
          </p>
          {product.requiresDelivery && (
            <span>
              {product.price.toLocaleString('cs-CZ')} za produkt +{' '}
              {product.shippingCost.toLocaleString('cs-CZ')} za dopravu
            </span>
          )}
        </div>
        <form
          className="ac-product-selection"
          onSubmit={(event) => {
            event.preventDefault()
            if (
              !isPurchased &&
              !inCart &&
              !isLoading &&
              product.isActive &&
              (!product.sizes.length || product.sizes.includes(size))
            )
              onAdd(product, size)
          }}
        >
          {product.sizes.length > 0 && (
            <label htmlFor={`variant-${product.id}`}>
              Varianta
              <select
                id={`variant-${product.id}`}
                value={size}
                onChange={(event) => setSize(event.target.value)}
                required
                disabled={isPurchased || inCart || isLoading}
              >
                <option value="" disabled>
                  Vyber variantu
                </option>
                {product.sizes.map((variant) => (
                  <option key={variant}>{variant}</option>
                ))}
              </select>
            </label>
          )}
          <button
            type="submit"
            className="ac-shop-button"
            disabled={isPurchased || inCart || isLoading || !product.isActive}
            aria-label={`${inCart ? 'V košíku' : isPurchased ? 'Již zakoupeno' : 'Přidat do košíku'}: ${product.name}`}
          >
            {isPurchased
              ? 'Již zakoupeno'
              : inCart
                ? 'V košíku'
                : !product.isActive
                  ? 'Nedostupné'
                  : isLoading
                    ? 'Chvíli strpení…'
                    : 'Přidat do košíku'}
            {!isPurchased && !inCart && product.isActive && !isLoading && (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                aria-hidden="true"
              >
                <path d="M5 12h14m-6-6 6 6-6 6" />
              </svg>
            )}
          </button>
        </form>
        {missing > 0 && !isPurchased && (
          <p className="ac-product-shortfall">Chybí ti {missing.toLocaleString('cs-CZ')} mincí.</p>
        )}
      </div>
    </article>
  )
}
